import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ViewToggle } from "@/components/common/ViewToggle";
import { Plus, Sparkles, Search, ChevronDown, X, Mail } from "lucide-react";
import { ListMembersGrid } from "./ListMembersGrid";
import { ListMembersList } from "./ListMembersList";
import { ListMembersPagination } from "./ListMembersPagination";
import { ListMember } from "@/types/list";
import { useRemoveCandidateFromList } from "@/hooks/lists/useListDetail";
import { matchesListMemberSearch } from "@/utils/searchUtils";
import { SendEmailModal } from "@/components/email/SendEmailModal";
import type { EmailRecipient } from "@/types/email";
import { Checkbox } from "@/components/ui/checkbox";
import { SkillsFilter } from "@/components/candidates/search/SkillsFilter";
import { normalizeSkill } from "@/utils/skillNormalization";

interface ListMembersContentProps {
  members: ListMember[];
  isLoading: boolean;
  listId: string;
  listName?: string;
  onMembersChange?: () => void;
  onAddCandidates?: () => void;
  onAISearch?: () => void;
}

export const ListMembersContent = ({ members, isLoading, listId, listName, onMembersChange, onAddCandidates, onAISearch }: ListMembersContentProps) => {
  const { t } = useTranslation();
  const removeMemberMutation = useRemoveCandidateFromList();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<string>>(new Set());
  const [emailMode, setEmailMode] = useState<'all' | 'selected'>('all');

  const handleAISearchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2000);
  };
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [sortField, setSortField] = useState<'name' | 'function' | 'email'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [skillsFilter, setSkillsFilter] = useState<string[]>([]);
  const [skillsOperator, setSkillsOperator] = useState<"AND" | "OR">("OR");

  useEffect(() => {
    setCurrentPage(1);
  }, [skillsFilter, skillsOperator]);

  const allSkills = useMemo(() => {
    const set = new Set<string>();
    (members ?? []).forEach(m => {
      m.candidate?.skills?.forEach(s => {
        const trimmed = s?.trim();
        if (trimmed)
          set.add(normalizeSkill(trimmed));
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [members]);

  const toggleCandidateSelection = (candidateId: string) => {
    setSelectedCandidateIds(prev => {
      const next = new Set(prev);
      if (next.has(candidateId))
        next.delete(candidateId);
      else
        next.add(candidateId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCandidateIds.size === filteredAndSortedMembers.length) {
      setSelectedCandidateIds(new Set());
    } else {
      setSelectedCandidateIds(new Set(filteredAndSortedMembers.map(m => m.candidate_id)));
    }
  };

  const getEmailRecipients = (): EmailRecipient[] => {
    const source = emailMode === 'selected'
      ? members.filter(m => selectedCandidateIds.has(m.candidate_id))
      : members;
    return source
      .filter(m => m.candidate?.email)
      .map(m => ({
        email: m.candidate!.email,
        name: m.candidate!.name || "",
        candidateId: m.candidate_id,
      }));
  };

  const handleSendEmailAll = () => {
    setEmailMode('all');
    setShowEmailModal(true);
  };

  const handleSendEmailSelected = () => {
    setEmailMode('selected');
    setShowEmailModal(true);
  };

  const ITEMS_PER_PAGE = 12;

  // Filter and sort members
  const filteredAndSortedMembers = useMemo(() => {
    let filtered = members ?? [];

    // Apply search filter using universal search
    if (searchTerm) {
      filtered = filtered.filter(member => matchesListMemberSearch(member, searchTerm));
    }

    // Apply skills filter (client-side, normalized match — mirrors main candidate list)
    if (skillsFilter.length > 0) {
      const normalizedFilter = skillsFilter.map(normalizeSkill);
      filtered = filtered.filter(member => {
        const memberSkills = (member.candidate?.skills ?? []).map(s => normalizeSkill(s));
        return skillsOperator === "AND"
          ? normalizedFilter.every(fs => memberSkills.includes(fs))
          : normalizedFilter.some(fs => memberSkills.includes(fs));
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.candidate.name?.toLowerCase() || '';
          bValue = b.candidate.name?.toLowerCase() || '';
          break;
        case 'function':
          aValue = a.candidate.function?.toLowerCase() || '';
          bValue = b.candidate.function?.toLowerCase() || '';
          break;
        case 'email':
          aValue = a.candidate.email?.toLowerCase() || '';
          bValue = b.candidate.email?.toLowerCase() || '';
          break;
        default:
          aValue = a.candidate.name?.toLowerCase() || '';
          bValue = b.candidate.name?.toLowerCase() || '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [members, searchTerm, sortField, sortOrder, skillsFilter, skillsOperator]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedMembers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedMembers = filteredAndSortedMembers.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to first page when search changes
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // Handle removing a member from the list
  const handleRemoveMember = async (memberId: string) => {
    try {
      await removeMemberMutation.mutateAsync({ listId, candidateId: memberId });
      onMembersChange?.();
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-10 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-48 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header with Action Buttons */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {t("lists.tabsContent.members.title", "List Members")}
          </h2>
          <div className="flex gap-2">
            <TooltipProvider>
              <Tooltip open={showTooltip} onOpenChange={setShowTooltip}>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 cursor-not-allowed"
                    onClick={handleAISearchClick}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t("lists.actions.aiSearch", "AI Search")}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Coming soon!</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              onClick={onAddCandidates}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("lists.actions.addCandidates", "Add Candidates")}
            </Button>
          </div>
        </div>

        <SendEmailModal
          open={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          recipients={getEmailRecipients()}
          listId={listId}
          listName={listName}
        />

        <div className="text-center py-12">
          <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-sm flex items-center justify-center mb-4">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t("lists.members.empty.title", "No candidates yet")}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t("lists.members.empty.description", "Add candidates to this list to get started")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Buttons at the top */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">
          {t("lists.tabsContent.members.title", "List Members")}
        </h2>
        <div className="flex gap-2">
          <TooltipProvider>
            <Tooltip open={showTooltip} onOpenChange={setShowTooltip}>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-600 cursor-not-allowed"
                  onClick={handleAISearchClick}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {t("lists.actions.aiSearch", "AI Search")}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Coming soon!</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            variant="outline"
            onClick={handleSendEmailAll}
            disabled={members.length === 0}
          >
            <Mail className="h-4 w-4 mr-2" />
            {t("lists.actions.sendEmail", "Send Email")}
          </Button>
          <Button
            onClick={onAddCandidates}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("lists.actions.addCandidates", "Add Candidates")}
          </Button>
        </div>
      </div>

      {/* Selected candidates action bar */}
      {selectedCandidateIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-sm border border-blue-200 dark:border-blue-800">
          <Checkbox
            checked={selectedCandidateIds.size === filteredAndSortedMembers.length}
            onCheckedChange={toggleSelectAll}
          />
          <span className="text-sm font-medium text-blue-500 dark:text-blue-300">
            {t("lists.members.selected", "{{count}} selected", { count: selectedCandidateIds.size })}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleSendEmailSelected}
            className="ml-auto"
          >
            <Mail className="h-3.5 w-3.5 mr-1.5" />
            {t("lists.actions.emailSelected", "Email Selected")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedCandidateIds(new Set())}
          >
            {t("common.clearSelection", "Clear")}
          </Button>
        </div>
      )}

      {/* Search Bar + Skills Filter */}
      <div className="flex items-center gap-3 w-full max-w-2xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder={t("lists.members.search.placeholder", "Search candidates...")}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchTerm && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 h-4 w-4"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <SkillsFilter
          skillsFilter={skillsFilter}
          setSkillsFilter={setSkillsFilter}
          skillsOperator={skillsOperator}
          setSkillsOperator={setSkillsOperator}
          allSkills={allSkills}
        />
        {skillsFilter.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSkillsFilter([]);
              setSkillsOperator("OR");
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-3.5 w-3.5 mr-1" />
            {t("candidates.filters.clearAll", "Clear filters")}
          </Button>
        )}
      </div>

      {/* View Options in white container */}
      <div className="flex justify-between items-center mb-4 bg-white dark:bg-gray-800 p-3 rounded-sm shadow-sm">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={selectedCandidateIds.size > 0 && selectedCandidateIds.size === filteredAndSortedMembers.length}
            onCheckedChange={toggleSelectAll}
            className="mr-1"
          />
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t("sortBar.sortBy")}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-1">
                {sortField === 'name' ? t('lists.members.sort.name', 'Name') : 
                 sortField === 'function' ? t('lists.members.sort.function', 'Function') : 
                 t('lists.members.sort.email', 'Email')}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => setSortField('name')}>{t('lists.members.sort.name', 'Name')}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortField('function')}>{t('lists.members.sort.function', 'Function')}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortField('email')}>{t('lists.members.sort.email', 'Email')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="ml-1"
          >
            {sortOrder === "asc" ? t("sortBar.ascending") : t("sortBar.descending")}
          </Button>
        </div>
        <ViewToggle 
          viewMode={viewMode}
          onViewModeChange={(mode) => {
            if (mode === 'card' || mode === 'list')
              setViewMode(mode);
          }}
          label={t("sortBar.view")}
          allowedModes={['card', 'list']}
        />
      </div>

      {/* Results Count */}
      {(searchTerm || skillsFilter.length > 0) && (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {t("lists.members.search.results", "{{count}} candidates found", { count: filteredAndSortedMembers.length })}
        </div>
      )}

      {/* Members Display */}
      {paginatedMembers.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t("lists.members.search.noResults", "No candidates found")}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {t("lists.members.search.noResultsDesc", "Try adjusting your search terms")}
          </p>
        </div>
      ) : (
        <>
          {viewMode === 'card' ? (
            <ListMembersGrid 
              members={paginatedMembers} 
              onMembersChange={onMembersChange}
              onRemoveMember={handleRemoveMember}
            />
          ) : (
            <ListMembersList 
              members={paginatedMembers} 
              onMembersChange={onMembersChange}
              onRemoveMember={handleRemoveMember}
            />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <ListMembersPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}

      {/* Send Email Modal */}
      <SendEmailModal
        open={showEmailModal}
        onClose={() => { setShowEmailModal(false); setSelectedCandidateIds(new Set()); }}
        recipients={getEmailRecipients()}
        listId={listId}
        listName={listName}
      />
    </div>
  );
};
